import { BookOpen, Code2, FileText, MessageCircle, Video } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { resolveConsultationUrl } from '@/lib/tenant-media-defaults'
import { cn } from '@/lib/utils'
import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'
import { useTenantConfig } from '@/hooks/use-tenant-config'

import { PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import NextImage from '@/components/NextImage'
import { Button } from '@/components/ui/button'

import { FORM_REGISTER_URL } from '@/constant/app'

import type { SectionElementSchema } from '@/types/element-schema.type'

// Icon mapping for CMS configurability
const iconMap: Record<string, React.ReactNode> = {
  Video: <Video className="w-6 h-6" />,
  BookOpen: <BookOpen className="w-6 h-6" />,
  Code2: <Code2 className="w-6 h-6" />,
  MessageCircle: <MessageCircle className="w-6 h-6" />,
  FileText: <FileText className="w-6 h-6" />,
}

interface LearningMethodItem {
  icon: string
  title: string
  description: string
}

interface LearningPathOverviewSectionProps {
  className?: string
  /** Data source: 'built-in' or 'custom' */
  dataSource?: 'built-in' | 'custom'
  /** Section heading (supports HTML) */
  heading?: string
  /** Methods section heading (supports HTML) */
  methodsHeading?: string
  /** Override default learning methods */
  methods?: LearningMethodItem[]
  /** Methods section image */
  methodsImage?: string
  /** Preview images array */
  previewImages?: Array<string | { src: string }>
  /** CTA button text */
  buttonText?: string
  /** CTA button URL */
  buttonUrl?: string
  // ── Layout Options ──
  /** Section padding size */
  sectionPadding?: 'compact' | 'default' | 'spacious'
  /** Methods area layout direction */
  methodsLayout?: 'side-by-side' | 'stacked'
  /** Gap between method items */
  methodsGap?: 'tight' | 'default' | 'loose'
  /** Preview images layout */
  previewLayout?: 'stacked' | 'grid'
  // ── Element Detail Options ──
  /** Icon visual style */
  iconStyle?: 'outlined' | 'filled' | 'minimal'
  /** Show methods section image */
  showMethodsImage?: boolean
  /** Show preview images */
  showPreviewImages?: boolean
  /** Show CTA button */
  showButton?: boolean
  // ── Color Options ──
  /** Heading text color */
  headingColor?: string
  /** Methods heading text color */
  methodsHeadingColor?: string
  /** Method item title color */
  methodTitleColor?: string
  /** Method item description color */
  methodDescColor?: string
  /** Section background color */
  bgColor?: string
  /** Icon background color */
  iconBgColor?: string
  /** Icon color */
  iconColor?: string
  /** Button background color */
  buttonBgColor?: string
  /** Button text color */
  buttonTextColor?: string
}

// Default learning methods data
function getDefaultMethods(siteName: string): LearningMethodItem[] {
  return [
    {
      icon: 'Video',
      title: 'Học trực tuyến qua Zoom',
      description: 'Tương tác trực tiếp với giảng viên đến từ các công ty hàng đầu.',
    },
    {
      icon: 'BookOpen',
      title: 'Hệ thống ôn tập thông minh',
      description:
        'Xem lại recording trên platform, làm quiz và code homework; bài làm được chấm tự động kết hợp phân tích & review code bằng LLM.',
    },
    {
      icon: 'Code2',
      title: 'Sandbox thực chiến',
      description: `Triển khai dự án industry-ready ngay trên môi trường dạng sản, không cần cài đặt. Dự án mở phòng thực tế - trải nghiệm hành trình học tại ${siteName} với mentor giàu kinh nghiệm thực chiến hàng ngày.`,
    },
    {
      icon: 'MessageCircle',
      title: 'Trợ giảng nhiệt tình',
      description: 'Luôn hỗ trợ xuyên suốt quá trình học tập.',
    },
    {
      icon: 'FileText',
      title: 'Blogs & webinars',
      description: 'Giúp học viên được cập nhật những kiến thức mới nhất.',
    },
  ]
}

const defaultMethods = getDefaultMethods('')

// Element schema for Puck editor
export const learningPathOverviewSectionSchema: SectionElementSchema = {
  componentType: 'LearningPathOverviewSection',
  label: 'Learning Path Overview',
  category: 'content',
  description: 'Overview section with learning methods and preview images',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      helpText: 'Main heading (supports HTML)',
      default: 'Tại sao nên học tại <span class="text-primary">Platform</span> ?',
    },
    {
      key: 'methodsHeading',
      type: 'textarea',
      label: 'Methods Heading',
      helpText: 'Learning methods section heading (supports HTML)',
      default: 'Phương thức học <span class="text-primary font-bold">đa dạng</span>',
    },
    // ── Data Source ──
    {
      key: 'dataSource',
      type: 'select',
      label: 'Data Source',
      helpText: 'Choose built-in content or custom items',
      options: [
        { label: 'Built-in (Default)', value: 'built-in' },
        { label: 'Custom Items', value: 'custom' },
      ],
      default: 'built-in',
    },
    {
      key: 'methods',
      type: 'array',
      label: 'Learning Methods',
      helpText: 'Add, edit, or remove method items (only used when Data Source is "Custom")',
      default: defaultMethods,
      children: [
        {
          key: 'icon',
          type: 'select',
          label: 'Icon',
          options: [
            { label: 'Video', value: 'Video' },
            { label: 'Book Open', value: 'BookOpen' },
            { label: 'Code', value: 'Code2' },
            { label: 'Message', value: 'MessageCircle' },
            { label: 'File/Document', value: 'FileText' },
          ],
          default: 'Video',
        },
        { key: 'title', type: 'text', label: 'Title', required: true },
        { key: 'description', type: 'textarea', label: 'Description', required: true },
      ],
    },

    // ── Layout Options ──
    {
      key: 'sectionPadding',
      type: 'select',
      label: 'Section Padding',
      helpText: 'Overall section padding size',
      options: [
        { label: 'Compact', value: 'compact' },
        { label: 'Default', value: 'default' },
        { label: 'Spacious', value: 'spacious' },
      ],
      default: 'default',
    },
    {
      key: 'methodsLayout',
      type: 'select',
      label: 'Methods Layout',
      helpText: 'Layout direction for learning methods area',
      options: [
        { label: 'Side by Side', value: 'side-by-side' },
        { label: 'Stacked', value: 'stacked' },
      ],
      default: 'side-by-side',
    },
    {
      key: 'methodsGap',
      type: 'select',
      label: 'Items Gap',
      helpText: 'Gap between learning method items',
      options: [
        { label: 'Tight', value: 'tight' },
        { label: 'Default', value: 'default' },
        { label: 'Loose', value: 'loose' },
      ],
      default: 'default',
    },
    {
      key: 'previewLayout',
      type: 'select',
      label: 'Preview Images Layout',
      helpText: 'How preview images are displayed',
      options: [
        { label: 'Stacked', value: 'stacked' },
        { label: 'Grid', value: 'grid' },
      ],
      default: 'stacked',
    },
    // ── Element Details ──
    {
      key: 'iconStyle',
      type: 'select',
      label: 'Icon Style',
      helpText: 'Visual style for method icons',
      options: [
        { label: 'Outlined', value: 'outlined' },
        { label: 'Filled', value: 'filled' },
        { label: 'Minimal', value: 'minimal' },
      ],
      default: 'outlined',
    },
    {
      key: 'showMethodsImage',
      type: 'boolean',
      label: 'Show Methods Image',
      helpText: 'Display image alongside the methods list',
      default: true,
    },
    {
      key: 'showPreviewImages',
      type: 'boolean',
      label: 'Show Preview Images',
      helpText: 'Display course preview images section',
      default: true,
    },
    {
      key: 'showButton',
      type: 'boolean',
      label: 'Show CTA Button',
      helpText: 'Display the call-to-action button',
      default: true,
    },
    // ── Text Content ──

    {
      key: 'methodsImage',
      type: 'image',
      label: 'Methods Image ',
      helpText: 'Image shown alongside the methods list',
      default: '/images/home/v6/mlflow.png',
    },
    {
      key: 'previewImages',
      type: 'array',
      label: 'Preview Images',
      helpText: 'List of course preview image URLs',
      default: [
        { src: '/images/home/v6/course-content-preview.png' },
        { src: '/images/home/v6/course-content-preview-2.png' },
      ],
      children: [{ key: 'src', type: 'image', label: ' ', default: '/images/home/v6/course-content-preview.png' }],
    },
    {
      key: 'buttonText',
      type: 'text',
      label: 'Button Text',
      helpText: 'Leave empty to use locale default',
    },
    {
      key: 'buttonUrl',
      type: 'text',
      label: 'Button URL',
      default: FORM_REGISTER_URL,
    },
    // ── Color Options ──
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'methodsHeadingColor',
      type: 'color',
      label: 'Methods Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'methodTitleColor',
      type: 'color',
      label: 'Method Title Color',
      helpText: 'Color for each method item title',
      default: '',
    },
    {
      key: 'methodDescColor',
      type: 'color',
      label: 'Method Description Color',
      helpText: 'Color for each method item description',
      default: '',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      helpText: 'Transparent = no background',
      default: 'transparent',
    },
    {
      key: 'iconBgColor',
      type: 'color',
      label: 'Icon Background Color',
      helpText: 'Background color for the icon container',
      default: '',
    },
    {
      key: 'iconColor',
      type: 'color',
      label: 'Icon Color',
      helpText: 'Color of the method icons',
      default: '',
    },
    {
      key: 'buttonBgColor',
      type: 'color',
      label: 'Button Background Color',
      helpText: 'CTA button background color',
      default: '',
    },
    {
      key: 'buttonTextColor',
      type: 'color',
      label: 'Button Text Color',
      helpText: 'CTA button text color',
      default: '',
    },
    // ── Advanced ──
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes for advanced styling',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(learningPathOverviewSectionSchema)

// Padding class maps
const paddingClassMap: Record<string, string> = {
  compact: 'px-4 gap-y-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 lg:py-12',
  default: 'px-4 gap-y-4 sm:px-6 md:px-8 py-8 sm:py-12 md:py-16 lg:py-20',
  spacious: 'px-4 gap-y-6 sm:px-8 md:px-12 py-12 sm:py-16 md:py-24 lg:py-32',
}

// Gap class maps for method items
const gapClassMap: Record<string, string> = {
  tight: 'gap-4 md:gap-y-2',
  default: 'gap-8 md:gap-y-4',
  loose: 'gap-12 md:gap-y-8',
}

// Icon style class maps
const iconStyleClassMap: Record<string, { container: string; iconClass: string }> = {
  outlined: {
    container: 'bg-red-50 border border-red-200',
    iconClass: 'text-red-500',
  },
  filled: {
    container: 'bg-red-500 border-none',
    iconClass: 'text-white',
  },
  minimal: {
    container: 'bg-transparent border-none',
    iconClass: 'text-red-500',
  },
}

function LearningPathOverviewSection({
  className,
  dataSource = 'built-in',
  heading,
  methodsHeading = 'Phương thức học <span class="text-primary font-bold">đa dạng</span>',
  methods: customMethods,
  methodsImage = '/images/home/v6/mlflow.png',
  previewImages = [
    { src: '/images/home/v6/course-content-preview.png' },
    { src: '/images/home/v6/course-content-preview-2.png' },
  ],
  buttonText,
  buttonUrl = FORM_REGISTER_URL,
  // Layout
  sectionPadding = 'default',
  methodsLayout = 'side-by-side',
  methodsGap = 'default',
  previewLayout = 'stacked',
  // Element details
  iconStyle = 'outlined',
  showMethodsImage = true,
  showPreviewImages = true,
  showButton = true,
  // Colors
  headingColor,
  methodsHeadingColor,
  methodTitleColor,
  methodDescColor,
  bgColor = 'transparent',
  iconBgColor,
  iconColor,
  buttonBgColor,
  buttonTextColor,
}: LearningPathOverviewSectionProps) {
  const { text } = useLocale()
  const { siteName, isFsdsDomain } = useBranding()
  const { customization } = useTenantConfig()
  const displayButtonText = buttonText || text.MAIN.INTRODUCTION.HEADING_4
  const resolvedButtonUrl = React.useMemo(() => {
    if (isFsdsDomain) {
      return buttonUrl
    }
    const tenantConsultationUrl = resolveConsultationUrl(customization)
    if (tenantConsultationUrl) {
      return tenantConsultationUrl
    }
    if (buttonUrl && buttonUrl !== FORM_REGISTER_URL) {
      return buttonUrl
    }
    return undefined
  }, [buttonUrl, customization, isFsdsDomain])

  // Determine which methods to use
  const showMethodsImageEnabled = showMethodsImage === true || (showMethodsImage as unknown) === 'true'
  const showPreviewImagesEnabled = showPreviewImages === true || (showPreviewImages as unknown) === 'true'
  const showButtonEnabled = showButton === true || (showButton as unknown) === 'true'

  const displayHeading = heading ?? `Tại sao nên học tại <span class="text-primary">${siteName}</span> ?`
  const displayMethods = dataSource === 'custom' && customMethods?.length ? customMethods : getDefaultMethods(siteName)

  const paddingClass = paddingClassMap[sectionPadding] || paddingClassMap.default
  const gapClass = gapClassMap[methodsGap] || gapClassMap.default
  const iconStyles = iconStyleClassMap[iconStyle] || iconStyleClassMap.outlined
  const hasBg = bgColor && bgColor !== 'transparent'

  // Methods layout
  const isMethodsStacked = methodsLayout === 'stacked'
  const methodsContainerClass = isMethodsStacked
    ? 'py-4 px-4 sm:px-6 lg:px-8 flex flex-col gap-12'
    : 'py-4 px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-12'
  const methodsListClass = isMethodsStacked ? 'w-full' : 'w-full lg:w-1/2'
  const methodsImageClass = isMethodsStacked ? 'w-full max-w-[800px] mx-auto' : 'w-full lg:w-1/2'

  // Preview layout
  const previewContainerClass =
    previewLayout === 'grid'
      ? 'w-full max-w-[1280px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-4'
      : 'w-full max-w-[1280px] mx-auto space-y-4'

  return (
    <Shell className={cn(paddingClass, className)} style={{ backgroundColor: hasBg ? bgColor : undefined }}>
      <PageHeaderHeading
        as="h2"
        size="lg"
        className="tracking-normal text-3xl md:text-4xl lg:text-5xl/tight md:leading-tight text-center lg:mb-12"
        dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(displayHeading) }}
        style={headingColor ? { color: headingColor } : undefined}
      />

      {/* Learning Methods Section */}
      <div className={methodsContainerClass}>
        <div className={methodsListClass}>
          <div className="mb-12">
            <h3
              className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl/tight md:leading-tight text-center lg:text-left mb-8 lg:mb-12 font-bold"
              dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(methodsHeading) }}
              style={methodsHeadingColor ? { color: methodsHeadingColor } : undefined}
            />
          </div>

          <div className={cn('grid', gapClass)}>
            {displayMethods.map((method, index) => (
              <div key={index} className="flex gap-4 sm:gap-6">
                <div className="flex-shrink-0">
                  <div
                    className={cn(
                      'flex items-center justify-center size-8 sm:size-10 rounded-lg',
                      iconStyles.container
                    )}
                    style={{
                      ...(iconBgColor ? { backgroundColor: iconBgColor, borderColor: iconBgColor } : {}),
                    }}
                  >
                    <span className={cn(iconStyles.iconClass)} style={iconColor ? { color: iconColor } : undefined}>
                      {iconMap[method.icon] || iconMap.Video}
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3
                    className="text-base sm:text-lg font-bold text-foreground mb-2"
                    style={methodTitleColor ? { color: methodTitleColor } : undefined}
                  >
                    {method.title}
                  </h3>
                  <p
                    className="text-xs sm:text-sm text-muted-foreground leading-relaxed"
                    style={methodDescColor ? { color: methodDescColor } : undefined}
                  >
                    {method.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        {showMethodsImageEnabled && (
          <div className={methodsImageClass}>
            <NextImage
              src={methodsImage || '/images/home/v6/mlflow.png'}
              alt="Learning Path Overview"
              width={624}
              height={480}
              quality={100}
              className="w-full h-full"
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          </div>
        )}
      </div>

      {/* Preview Images Section */}
      {showPreviewImagesEnabled && (
        <div className={previewContainerClass}>
          {previewImages
            .map(item => (typeof item === 'string' ? item : item?.src))
            .filter((url): url is string => !!url)
            .map((src, index) => (
              <NextImage
                key={index}
                src={src}
                alt={`Course Content Preview ${index + 1}`}
                width={1280}
                height={606}
                quality={100}
                className="w-full h-full"
                sizes="100vw"
                classNames={{
                  image: 'select-none object-cover',
                }}
              />
            ))}
        </div>
      )}

      {/* CTA Button */}
      {showButtonEnabled &&
        (resolvedButtonUrl ? (
          <Button
            className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 text-base sm:text-lg rounded-md w-full sm:w-auto mx-auto mt-10"
            size="lg"
            asChild
            style={{
              backgroundColor: buttonBgColor || undefined,
              color: buttonTextColor || undefined,
            }}
          >
            <Link href={resolvedButtonUrl} target="_blank" rel="noopener noreferrer">
              {displayButtonText}
            </Link>
          </Button>
        ) : (
          <Button
            className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 text-base sm:text-lg rounded-md w-full sm:w-auto mx-auto mt-10"
            size="lg"
            disabled
            style={{
              backgroundColor: buttonBgColor || undefined,
              color: buttonTextColor || undefined,
            }}
          >
            {displayButtonText}
          </Button>
        ))}
    </Shell>
  )
}

export default LearningPathOverviewSection
